import {BASE_URL_PLACEHOLDER, ROUTES} from '@/config/constants'
import {FeatureConfig} from '@/config/featureToggles'
import {getApplications, getCountries} from '@/helpers/envConfig'
// import {oktaAuth} from '@/helpers/oktaConfig'
import {store} from '@/store'
import axios, {AxiosError, HttpStatusCode} from 'axios'
import {z} from 'zod'

export enum HTTPMethod {
  DELETE = 'DELETE',
  GET = 'GET',
  POST = 'POST',
  PATCH = 'PATCH',
  PUT = 'PUT',
}

const onUnauthorisedError = async () => {
  localStorage.removeItem('msalAccessToken')
  window.location.href = ROUTES.SESSION_EXPIRED
  // await oktaAuth.signOut()
}

export const handleNetworkErrors = async (error: AxiosError) => {
  const isCanceled = error.code === AxiosError.ERR_CANCELED

  if (isCanceled) return Promise.reject(error)

  const isUnauthorized = error.response?.status === HttpStatusCode.Unauthorized
  const isForbidden = error.response?.status === HttpStatusCode.Forbidden
  const isNetworkError = error.code === AxiosError.ERR_NETWORK

  if (isForbidden) {
    window.location.href = ROUTES.ERROR_403
  } else if (isUnauthorized) {
    try {
      await onUnauthorisedError()
    } catch (signOutError) {
      console.error('onUnauthorisedError::ERROR', signOutError)
    }
    // window.location.reload()
  } else if (isNetworkError) {
    window.location.href = ROUTES.ERROR_500
  }

  return Promise.reject(error)
}

const axiosiInstance = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
})
axiosiInstance.interceptors.request.use(
  config => config,
  error => handleNetworkErrors(error)
)
axiosiInstance.interceptors.response.use(
  response => response,
  error => handleNetworkErrors(error)
)

export default function api<Request, Response>({
  method = HTTPMethod.GET,
  path,
  requestSchema,
  responseSchema,
  signal,
}: {
  method?: HTTPMethod
  path: string
  requestSchema: z.ZodType<Request>
  responseSchema: z.ZodType<Response>
  signal?: AbortSignal
}): (data: Request) => Promise<Response> {
  return (requestData: Request) => {
    try {
      requestSchema.parse(requestData)
    } catch (error) {
      console.error(`requestSchema.parse::ERROR [${method}] [${path}]`, (error as z.ZodError).message)
      throw error
    }

    async function apiCall() {
      const {url, baseURL} = pathAdapter(path)
      const {application, shellActive} = store.getState().common
      const token = localStorage.getItem('msalAccessToken')

      const response = await axiosiInstance.request({
        signal,
        baseURL,
        url,
        headers: {
          Authorization: `Bearer ${token}`,
          country: store.getState().journeyConfig.country || getCountries()[0],
          Application: shellActive ? 'B2B' : application || getApplications()[0],
        },
        method,
        [method === HTTPMethod.GET ? 'params' : 'data']: requestData,
      })

      try {
        return responseSchema.parse(response.data)
      } catch (error) {
        console.error(`responseSchema.parse::ERROR [${method}] [${path}]`, (error as z.ZodError).message, response.data)
        throw error
      }
    }

    return apiCall()
  }
}

const pathAdapter = (path: string) => {
  let baseURL = import.meta.env[FeatureConfig.apiUrl]

  const adminPortalEndpointPlaceholder = BASE_URL_PLACEHOLDER.ADMIN_PORTAL_ENDPOINT
  const adminPortalEndpoint = import.meta.env[FeatureConfig.apiAdminUrlEndpointSuffix]

  const adminPortalPlaceholder = BASE_URL_PLACEHOLDER.ADMIN_PORTAL
  const isAdminPortalUrl = path.includes(adminPortalPlaceholder)
  if (isAdminPortalUrl) {
    baseURL = import.meta.env[FeatureConfig.apiAdminUrl]
  }

  const realTimeEventsPlaceholder = BASE_URL_PLACEHOLDER.EVENTS
  const isRealTimeEventsUrl = path.includes(realTimeEventsPlaceholder)
  if (isRealTimeEventsUrl) {
    baseURL = import.meta.env[FeatureConfig.apiEventsUrl]
  }

  const url = path
    .replace(adminPortalPlaceholder, '')
    .replace(realTimeEventsPlaceholder, '')
    .replace(adminPortalEndpointPlaceholder, adminPortalEndpoint)

  return {
    url,
    baseURL,
    isAdminPortalUrl,
    isRealTimeEventsUrl,
  }
}
